import { Flicking as CustomFlicking } from '#atoms';
import { Modal } from '#molecules';
import '@egjs/react-flicking/dist/flicking.css';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Button } from '../Button';
import { Flex } from '../Flex';

type DatePickerInputProps = {
  date?: Date;
  onChange?: (date: Date) => void;
  label?: string;
  title?: string;
  disabled?: boolean;
  required?: boolean;
  fullWidth?: boolean;
  placeholder?: string;
  onlyYearMonth?: boolean;
  style?: React.CSSProperties;
  children?: React.ReactNode;
};

const selections = {
  year: new Array(120).fill(0).map((_, i) => new Date().getFullYear() - i),
  month: new Array(12).fill(0).map((_, i) => i + 1),
  date: new Array(31).fill(0).map((_, i) => i + 1)
};

type Payload = {
  year: number;
  month: number;
  date: number;
};

export function DatePickerInput({
  label,
  date,
  onChange,
  title,
  disabled,
  required,
  fullWidth,
  placeholder,
  onlyYearMonth,
  children
}: DatePickerInputProps) {
  const defaultPayload = {
    year: new Date().getFullYear(),
    month: 1,
    date: 1
  };
  const [isOpen, setIsOpen] = useState(false);
  const [payload, setPayload] = useState<Payload | undefined>({
    year: selections.year[0],
    month: selections.month[0],
    date: selections.date[0]
  });

  const formatted = dayjs(date).format(
    onlyYearMonth ? 'YYYY.MM' : 'YYYY.MM.DD'
  );

  const closeModal = () => {
    setIsOpen(false);
  };

  const handlePayloadChange = (key: keyof Payload, value: string) => {
    setPayload((payload = defaultPayload) => ({
      ...payload,
      [key]: Number(value)
    }));
  };

  const handleOk = () => {
    if (!payload) return;

    const newDate = new Date(payload.year, payload.month - 1, payload.date);

    if (onChange) onChange(newDate);
    closeModal();
  };

  useEffect(() => {
    if (!date) return;

    // date가 invalid한 경우
    if (isNaN(date.getTime())) {
      setPayload(defaultPayload);
      return;
    }

    setPayload({
      year: date.getFullYear(),
      month: date.getMonth() + 1,
      date: date.getDate()
    });
  }, [date]);

  return (
    <>
      <Wrapper
        style={{
          width: fullWidth ? '100%' : 'auto'
        }}
      >
        {label && (
          <Label>
            {label}
            {required && <RequiredMark>*</RequiredMark>}
          </Label>
        )}
        <div
          onClickCapture={() => {
            if (disabled) return;
            setIsOpen(true);
          }}
        >
          {children ? (
            children
          ) : (
            <Input>
              {Boolean(date) ? (
                formatted
              ) : (
                <Placeholder>{placeholder}</Placeholder>
              )}
            </Input>
          )}
        </div>
      </Wrapper>
      {isOpen && (
        <Modal onClose={closeModal} style={{ maxWidth: '320px' }}>
          <ModalWrapper direction="column" fullWidth>
            <ModalTitle>{title}</ModalTitle>
            <FlickingWrapper>
              <CustomFlicking
                list={selections.year.map(String)}
                defaultIndex={selections.year.findIndex(
                  y => y === date?.getFullYear()
                )}
                bgColor="transparent"
                borderColor="transparent"
                wrapperProps={{ style: { flex: 1 } }}
                onChange={handlePayloadChange.bind(null, 'year')}
              />
              <CustomFlicking
                list={selections.month.map(String)}
                defaultIndex={
                  date
                    ? selections.month.findIndex(m => m === date.getMonth() + 1)
                    : -1
                }
                bgColor="transparent"
                borderColor="transparent"
                wrapperProps={{ style: { flex: 1 } }}
                onChange={handlePayloadChange.bind(null, 'month')}
              />
              {!onlyYearMonth && (
                <CustomFlicking
                  list={selections.date.map(String)}
                  defaultIndex={selections.date.findIndex(
                    d => d === date?.getDate()
                  )}
                  bgColor="transparent"
                  borderColor="transparent"
                  wrapperProps={{ style: { flex: 1 } }}
                  onChange={handlePayloadChange.bind(null, 'date')}
                />
              )}
            </FlickingWrapper>
            <Flex gap={12}>
              <Button size="sm" background="anti" onClick={closeModal}>
                닫기
              </Button>
              <Button size="sm" onClick={handleOk}>
                확인
              </Button>
            </Flex>
          </ModalWrapper>
        </Modal>
      )}
    </>
  );
}

const Wrapper = styled.div``;

const Label = styled.div`
  font-weight: 500;
  color: ${({ theme }) => theme.palette.gray25};
  margin-bottom: 8px;
`;

const Input = styled.div`
  cursor: pointer;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.palette.gray400};
  padding: 14px 16px;
  color: ${({ theme }) => theme.palette.gray15};
`;

const ModalWrapper = styled(Flex)``;

const FlickingWrapper = styled.div`
  margin: 1rem 0;
  display: flex;
`;

const ModalTitle = styled.div`
  color: ${({ theme }) => theme.palette.gray15};
  font-size: 20px;
  font-weight: 600;
  text-align: left;
  margin-left: 4px;
`;

const RequiredMark = styled.span`
  color: ${({ theme }) => theme.palette.brand300};
  font-weight: 500;
`;

const Placeholder = styled.span`
  color: ${({ theme }) => theme.palette.gray300};
`;
